import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'

/**
 * "The Executive's Guide to Sustainable Leadership" — the e-book's own page.
 *
 * Built to the design of the digitopia.co page it replaces, sent as four
 * screenshots on 2026-08-28 with *"referans alarak ilgili sayfayı tastamam
 * şekilde, aynısını, tıpkısını tasarla"*. Band for band that page's: the
 * two-column hero with the spiral-staircase artwork beside it, the grey "What's
 * Inside" card with the pair of cover mockups and the five bullets, the "Why
 * Download" card with the stacked copies and three bullets, and the centred
 * "Inspire. Lead. Transform. Download Your Free Ebook Now" ask. The copy is that
 * page's copy, verbatim.
 *
 * Two places where the reference and this site disagree, and what this page does
 * about each:
 *
 *  * **The download is a direct link to the PDF, not a gated form.** The
 *    reference puts a nine-field HubSpot form between the reader and the file;
 *    no form on this site submits anywhere yet, so reproducing it would mean the
 *    page's only call to action silently drops every lead. The `check` below
 *    says so.
 *  * **No testimonial closes the page**, because the reference does not have
 *    one: after the closing ask it goes straight to the site-wide "Let's Get in
 *    Touch" band, which `AppShell` already draws under every page here.
 *
 * **The slug is the reference's, not the catalogue's.** The screenshots came
 * from `digitopia.co/impact-labs/ebooks/executives-guide-to-sustainable-leadership/`
 * and carry that address in their filenames, so the real address drops the
 * leading "the" that the title rule in `content/ebooks.ts` had derived. That
 * entry now carries the confirmed slug and points its card here.
 */
export const route: RouteDef = {
  id: 'ebookExecutivesGuideToSustainableLeadership',
  path: 'impact-labs/ebooks/executives-guide-to-sustainable-leadership',
  priority: 0.7,
}

/**
 * The PDF, in the `website-documents` bucket — the same object the e-book
 * catalogue already points its card at. It is a local constant rather than a key
 * in `content/pages/links.ts`, because a page's own download is nobody else's
 * and five concurrent runs appending to that file is a push conflict.
 */
const EBOOK_PDF
  = 'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/ebook/'
  + '396695c4-afaa-464c-9148-89d8044403c2-The%20Executive_s%20Guide%20to%20Sustainable%20Leadership.pdf'

export const ebookExecutivesGuideToSustainableLeadership: WrittenPage = {
  navLabel: 'The Executive’s Guide to Sustainable Leadership',
  title: 'The Executive’s Guide to Sustainable Leadership',
  description:
    'Driving change from the top: essential insights and strategies for executives to embed '
    + 'sustainability into their core business strategy. Download the e-book from Digitopia '
    + 'Impact Labs.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Driving Change from the Top',
      headline: 'The Executive’s Guide to Sustainable Leadership',
      body: [
        'Executive leadership in sustainability is critical for long-term success. Our latest '
          + 'ebook provides essential insights and strategies for executives to embed '
          + 'sustainability into their core business strategy.',
      ],
      ctas: [{ label: 'Download', href: EBOOK_PDF }],
      photo: {
        src: '/photo/sustainable-leadership-ebook-artwork.webp',
        alt:
          'The e-book’s cover artwork: a pale blue spiral staircase seen from directly above, '
          + 'curling around a bed of bright green planting, with one person reaching towards it.',
      },
      // The artwork is square and the site's hero box is 3:2, which would cut
      // the staircase off on both sides.
      photoFrame: 'square',
      /*
        Clause 1 — THE DOWNLOAD IS NOT GATED — resolved 2026-08-30. It said no
        form on this site submits anywhere and the Send button is disabled on
        purpose. Both stopped being true today: the form posts to
        `website-form-lead`, the submission opens a task on its page's own
        counterpart, and every Download here carries `data-gated-download`, so
        the reader is asked for an email and the PDF is sent to it. No HubSpot
        form id is needed for any of it. What is left is clause 2.
      */
      check:
        'THE ARTWORK IS 768–900px WIDE. Those are the largest copies of the '
        + 'cover artwork and the two mockups anybody has sent; every other hero on '
        + 'the site is 2560px, so this one is painted at roughly its own width on a '
        + 'laptop with nothing left over for a retina screen. It is generated art '
        + 'rather than a photograph, so a bigger export should exist wherever it was '
        + 'made:\n'
        + '     node apps/website/scripts/attach.mjs <file> photo-src/sustainable-leadership-ebook-artwork.webp\n'
        + '     pnpm --filter @digitopia/website images\n'
        + '   and nothing in this file changes.',
    },
    {
      kind: 'cta',
      tone: 'card',
      // Mockup left, offer right — the reference's order, and it alternates with
      // the card under it.
      flip: true,
      heading: 'What’s Inside',
      points: [
        'The role of executives in driving sustainability and setting the vision.',
        'Strategies for creating a sustainable culture within your organization.',
        'Insights on engaging stakeholders and building impactful partnerships.',
        'Case studies from leading companies like Levi Strauss & Co.',
        'Practical steps for integrating sustainability into your business operations.',
      ],
      ctas: [{ label: 'Download the Ebook', href: EBOOK_PDF }],
      photo: {
        src: '/photo/sustainable-leadership-ebook-covers.webp',
        alt:
          'Two copies of The Executive’s Guide to Sustainable Leadership, one behind the other, '
          + 'on a pale gradient.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Why Download',
      points: [
        'Understand the importance of sustainability in executive leadership.',
        'Learn how to inspire and mobilize your organization towards sustainable practices.',
        'Discover actionable strategies to enhance your company’s reputation and resilience.',
      ],
      ctas: [{ label: 'Download the Report', href: EBOOK_PDF }],
      photo: {
        src: '/photo/sustainable-leadership-ebook-stack.webp',
        alt:
          'A stack of printed copies of The Executive’s Guide to Sustainable Leadership seen from '
          + 'above.',
      },
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Inspire. Lead. Transform. Download Your Free Ebook Now',
      sub:
        'Elevate your leadership and drive transformative change. Get your copy of “The '
        + 'Executive’s Guide to Sustainable Leadership: Driving Change from the Top” and lead '
        + 'your organization towards a sustainable and prosperous future.',
      cta: { label: 'Download the Ebook', href: EBOOK_PDF },
    },
  ],
}
